import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui";
import {
  ContentPageTheme,
  ContentPageThemeServices,
  Form,
  FormServices,
  ReviewWidgetConfig,
  ReviewWidgetConfigServices,
} from "@/services/bedrock";
import { ReviewWidgetConfigForm } from "./components/ReviewWidgetConfigForm";
import { ReviewWidgetPreview } from "./components/ReviewWidgetPreview";
import { ReviewWidgetPreviewControls } from "./components/ReviewWidgetPreviewControls";
import { useReviewWidgetPreview } from "./components/useReviewWidgetPreview";

interface ReviewWidgetContentProps {
  config: ReviewWidgetConfig;
  updateConfig: (updates: Partial<ReviewWidgetConfig>) => void;
  themes: ContentPageTheme[];
  forms: Form[];
}

function ReviewWidgetContent({ config, updateConfig, themes, forms }: ReviewWidgetContentProps) {
  const { controlsProps, previewProps } = useReviewWidgetPreview({ config, themes, forms });

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] lg:min-h-[calc(100vh-14rem)] lg:min-w-0">
      <div className="@container p-1 flex flex-col gap-6 min-w-0 lg:min-h-0 lg:overflow-y-auto no-scrollbar">
        <ReviewWidgetConfigForm config={config} updateConfig={updateConfig} themes={themes} forms={forms} />
        <ReviewWidgetPreviewControls {...controlsProps} />
      </div>

      <div className="p-1 min-w-0 lg:min-h-0 lg:overflow-y-auto no-scrollbar">
        <ReviewWidgetPreview {...previewProps} />
      </div>
    </div>
  );
}

export function ReviewWidget() {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState<ReviewWidgetConfig | null>(null);

  const { data: formsData, isLoading: isLoadingForms } = useQuery({
    queryKey: ["forms", { page: 1, perPage: 100 }],
    queryFn: () => FormServices.getForms({ page: 1, perPage: 100, active: true }),
  });

  const { data: themesData, isLoading: isLoadingThemes } = useQuery({
    queryKey: ["contentPageThemes", { page: 1, perPage: 100 }],
    queryFn: () => ContentPageThemeServices.getPageThemes({ page: 1, perPage: 100 }),
  });

  const formsList = formsData?.content || [];
  const themesList = themesData?.content || [];

  useEffect(() => {
    setLoading(true);
    ReviewWidgetConfigServices.getConfigs()
      .then(async configs => {
        if (configs.length > 0) {
          setConfig(configs[0]);
        } else {
          const created = await ReviewWidgetConfigServices.createConfig({
            approvedForSocialOnly: true,
            reviewSortType: "featured",
            autoScrollEnabled: true,
          });
          setConfig(created);
        }
      })
      .catch(error => {
        console.error(error);
        toast.error("Error loading review widget settings");
      })
      .finally(() => setLoading(false));
  }, []);

  const updateConfig = (updates: Partial<ReviewWidgetConfig>) => {
    if (!config) return;
    setConfig({ ...config, ...updates });
  };

  const isInitializing = loading || isLoadingForms || isLoadingThemes;

  return (
    <div className="flex flex-col gap-4">
      {isInitializing || !config ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="w-full h-10 rounded-lg" />
          <Skeleton className="w-full h-24 rounded-lg" />
          <Skeleton className="w-full h-24 rounded-lg" />
        </div>
      ) : (
        <ReviewWidgetContent config={config} updateConfig={updateConfig} themes={themesList} forms={formsList} />
      )}
    </div>
  );
}
